import { CDN } from './cdn';
import { PV2_STAGES } from '../content/forAgenciesPage';
import { ENT_PIPE_STAGES } from '../content/forEnterprisePage';

export type HeroMedia = { type: 'image' | 'video'; url: string };

export type PageMediaManifest = {
  hero: HeroMedia | null;
  prefetch: { images: string[]; videos: string[] };
};

const isString = (v: string | undefined): v is string => Boolean(v);
const uniq = (urls: string[]) => [...new Set(urls)];
const videoUrls = (items: { video?: string }[]) =>
  uniq(items.map((s) => s.video).filter(isString));

const HOME_MANIFEST: PageMediaManifest = {
  hero: { type: 'image', url: CDN.hero.noir },
  prefetch: {
    // Next reel still only. Feature/audience MP4s load when they enter view.
    images: [CDN.hero.drama],
    videos: [],
  },
};

const FILMMAKERS_MANIFEST: PageMediaManifest = {
  hero: { type: 'video', url: CDN.videos.filmMaker },
  prefetch: {
    images: Object.values(CDN.filmmakers),
    videos: [CDN.videos.previs, CDN.videos.shortFilm, CDN.videos.pitch, CDN.videos.filmSpecSpot],
  },
};

const AGENCIES_MANIFEST: PageMediaManifest = {
  hero: { type: 'video', url: CDN.videos.agencyHeroPortrait },
  prefetch: {
    images: Object.values(CDN.agencies),
    videos: videoUrls(PV2_STAGES),
  },
};

const ENTERPRISE_MANIFEST: PageMediaManifest = {
  hero: { type: 'video', url: CDN.videos.enterpriseHeroPortrait },
  prefetch: {
    images: [],
    videos: uniq([
      ...videoUrls(ENT_PIPE_STAGES),
      CDN.videos.filmMakerAlt,
      CDN.videos.enterpriseTeamUseCase,
      CDN.videos.enterpriseSalesEnablement,
      CDN.videos.ingestAlt,
    ]),
  },
};

const PRODUCT_MANIFESTS: Record<string, PageMediaManifest> = {
  '/product/script-editor': {
    hero: { type: 'video', url: CDN.videos.scriptEditor },
    prefetch: { images: [], videos: [] },
  },
  '/product/shot-control': {
    hero: { type: 'video', url: CDN.videos.shotControl },
    prefetch: { images: [], videos: [] },
  },
  '/product/models': {
    hero: { type: 'video', url: CDN.videos.direction },
    prefetch: { images: [], videos: [] },
  },
  '/product/ai-video-continuity': {
    hero: { type: 'video', url: CDN.videos.continuity },
    prefetch: { images: [], videos: [] },
  },
};

const ROUTE_MANIFESTS: Record<string, PageMediaManifest> = {
  '/': HOME_MANIFEST,
  '/for-filmmakers': FILMMAKERS_MANIFEST,
  '/for-agencies': AGENCIES_MANIFEST,
  '/for-enterprise': ENTERPRISE_MANIFEST,
  ...PRODUCT_MANIFESTS,
};

export function getPageMedia(pathname: string): PageMediaManifest | null {
  const path = pathname.replace(/\/$/, '') || '/';
  return ROUTE_MANIFESTS[path] ?? null;
}

export function getPageVideoUrls(pathname: string): string[] {
  const manifest = getPageMedia(pathname);
  if (!manifest) return [];
  const hero = manifest.hero?.type === 'video' ? [manifest.hero.url] : [];
  return uniq([...hero, ...manifest.prefetch.videos]);
}

/** Prerender the hero image hint. Videos are loaded lazily by AutoplayVideo. */
export function getHeroPreloadElements(pathname: string) {
  const manifest = getPageMedia(pathname);
  const elements: { type: 'link'; props: Record<string, string> }[] = [];

  if (manifest?.hero?.type === 'image') {
    elements.push({
      type: 'link',
      props: { rel: 'preload', as: 'image', href: manifest.hero.url },
    });
  }

  return elements;
}
